import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './CyberServicesCardsAcademy.css';
import { services } from './CardsData';
import { Service } from './types';
import { motion, useMotionValue, useMotionTemplate } from 'framer-motion';
import { generateRandomString } from '../../ui/evervault-card';
import { GridPatternCard, GridPatternCardBody } from '../../ui/card-with-ellipsis-pattern';

const CyberServicesCardsAcademy: React.FC = () => {
  const { t } = useTranslation();
  const serviceRows: Service[][] = [];
  for (let i = 0; i < services.length; i += 2) {
    serviceRows.push(services.slice(i, i + 2));
  }

  return (
    <div className="services-content-wrapper">
      <div className="services-section">
        {serviceRows.map((row, rowIndex) => (
          <div className="service-row" key={rowIndex}>
            {row.map((service) => (
              <ServiceCard service={service} key={service.id} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const ServiceCard: React.FC<{ service: Service }> = ({ service }) => {
  const { t, i18n } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  
  // Get current language and create URL prefix
  const currentLanguage = i18n.language;
  const urlPrefix = currentLanguage === 'en' ? '/en' : '';
  const serviceLink = `${urlPrefix}${service.link}`;
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const [randomString, setRandomString] = useState("");

  useEffect(() => {
    setRandomString(generateRandomString(1500));
  }, []);

  function onMouseMove({ currentTarget, clientX, clientY }: React.MouseEvent<HTMLDivElement>) {
    const { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);

    if (isHovered) {
      setRandomString(generateRandomString(1500));
    }
  }

  const maskImage = useMotionTemplate`radial-gradient(250px at ${mouseX}px ${mouseY}px, white, transparent)`;
  
  // Translate service content
  const translatedTitle = t(`academy.cards.${service.id}.title`, { defaultValue: service.title });
  const translatedSubtitle = service.subtitle ? t(`academy.cards.${service.id}.subtitle`, { defaultValue: service.subtitle }) : undefined;
  const translatedDescription = t(`academy.cards.${service.id}.description`, { defaultValue: service.description });
  const translatedAlt = t(`academy.cards.${service.id}.alt`, { defaultValue: service.alt });
  const translatedLearnMore = t('common.learnMore', { defaultValue: 'En savoir plus' });
  
  return (
    <div 
      className="service-cyber" 
      style={{ position: 'relative', overflow: 'hidden' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseMove={onMouseMove}
    >
      <motion.div
        className="tw-absolute tw-inset-0 tw-bg-gradient-to-r tw-from-green-500 tw-to-blue-700 tw-backdrop-blur-xl"
        style={{ 
          maskImage, 
          WebkitMaskImage: maskImage,
          opacity: isHovered ? 1 : 0,
          transition: 'opacity 0.5s ease',
          zIndex: 0
        }}
      />
      <motion.div
        className="tw-absolute tw-inset-0 tw-mix-blend-overlay"
        style={{ 
          maskImage, 
          WebkitMaskImage: maskImage,
          opacity: isHovered ? 1 : 0,
          transition: 'opacity 0.5s ease',
          zIndex: 0
        }}
      >
        <p className="tw-absolute tw-inset-x-0 tw-text-xs tw-h-full tw-break-words tw-whitespace-pre-wrap tw-text-white tw-font-mono tw-font-bold">
          {randomString}
        </p>
      </motion.div>

      <a 
        href={serviceLink}
        className="tw-absolute tw-inset-0 tw-bg-black/50"
        style={{ 
          opacity: isHovered ? 1 : 0,
          transition: 'opacity 0.5s ease',
          zIndex: 1,
          cursor: 'pointer'
        }}
      />

      {/* Contenu de la carte avec z-index élevé pour rester visible */}
      <a 
        href={serviceLink} 
        style={{ position: 'relative', zIndex: 2, display: 'block', textDecoration: 'none', color: 'inherit' }}
      >
        <div className="image-title-container">
          <div className="icon">
            <img src={service.icon} alt={translatedAlt} />
          </div>
          <h2
            className="blue-h2"
            dangerouslySetInnerHTML={{ __html: translatedTitle }}
          />
        </div>
        <div className="content">
          {translatedSubtitle && (
            <h3 className="service-subtitle">{translatedSubtitle}</h3>
          )}
          <p className="service-text-cyber">{translatedDescription}</p>
          <span className="learn-more">{translatedLearnMore}</span>
        </div>
      </a>
    </div>
  );
};

export default CyberServicesCardsAcademy;